import { useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createFileRoute, Link } from '@tanstack/react-router';
import { OPS_SERVICE_LABELS, opsDisplayNo, computeOpsSla } from '@cortex/ops-core';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Select, type SelectOption } from '@cortex/web-design-system/components/ui/Select';
import { TabButton } from '../components/ui/Tabs/TabButton.js';
import { TabGroup } from '../components/ui/Tabs/TabGroup.js';
import { useAuth } from '../hooks/useAuth.js';
import { useOpsRequestsPage, type OpsStatusFilter } from '../hooks/useOps.js';
import { flattenOpsRequestPages } from '../lib/ops-request-pages.js';
import { CategoryBadge, SlaChip, StateBadge } from '../components/features/ops/OpsPrimitives.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops マイページ。ログインユーザーの nickName（Firestore users.nickName）を基準に「自分が起票した依頼（依頼者ビュー）」と「自分が対応する依頼（対応者ビュー）」を 2 レンズで切り替え表示する。依頼者 / 対応者は手入力なので大小無視 + トリムで一致判定する（backend 側）。やり取りは Slack、結果はここで確認できる。各カードは種別（調査依頼 / 状態変更 / データ修正 / SQL レビュー）を色付きバッジで表示する
 * @graph-connects useOpsRequestsPage [calls] 依頼者 / 対応者で絞り込んだ依頼のページ取得
 * @graph-connects useAuth [calls] ログインユーザーの nickName を取得して絞り込みに使う
 * @graph-connects flattenOpsRequestPages [calls] 取得済みページを id 一意の行リストへ畳む
 */
export const Route = createFileRoute('/ops/my-requests')({
  component: OpsMyRequestsPage,
});

/** 表示レンズ（filed = 自分が起票 / assigned = 自分が対応）。 */
type Lens = 'filed' | 'assigned';

/** ステータス絞り込みの選択肢の値と翻訳キー（両レンズ共通。label は t() で解決）。 @graph-connects none */
const STATUS_OPTIONS: { value: OpsStatusFilter; labelKey: string }[] = [
  { value: 'all', labelKey: 'ops.myRequests.statuses.all' },
  { value: 'open', labelKey: 'ops.myRequests.statuses.open' },
  { value: 'investigating', labelKey: 'ops.myRequests.statuses.investigating' },
  { value: 'blocked', labelKey: 'ops.myRequests.statuses.blocked' },
  { value: 'closed', labelKey: 'ops.myRequests.statuses.closed' },
];

/** @graph-connects none */
function OpsMyRequestsPage(): ReactNode {
  const { t } = useTranslation();
  const statusOptions: SelectOption[] = STATUS_OPTIONS.map((o) => ({
    value: o.value,
    label: t(o.labelKey),
  }));
  const nick = useAuth().user?.nickName;
  const enabled = nick !== undefined && nick.trim() !== '';
  const [lens, setLens] = useState<Lens>('filed');
  const [status, setStatus] = useState<OpsStatusFilter>('all');

  // 2 レンズとも nickName が解決できたときだけフェッチする（未解決なら enabled=false で空振りを防ぐ）。
  // ステータス絞り込みは両レンズに適用（タブの件数もフィルタ後の総数を反映する）。
  const filed = useOpsRequestsPage(status, 'all', { requester: nick, enabled });
  const assigned = useOpsRequestsPage(status, 'all', { assignee: nick, enabled });

  if (!enabled) {
    return (
      <Card>
        <div className="p-6 text-center text-sm text-[var(--color-text-muted)]">
          {t('ops.myRequests.nickNameUnresolved')}
        </div>
      </Card>
    );
  }

  const active = lens === 'filed' ? filed : assigned;
  const rows = flattenOpsRequestPages(active.data);
  const filedTotal = filed.data?.pages[0]?.total ?? 0;
  const assignedTotal = assigned.data?.pages[0]?.total ?? 0;
  const { hasNextPage, isFetchingNextPage, fetchNextPage, isLoading, isError, refetch } = active;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <TabGroup ariaLabel={t('ops.myRequests.lensLabel')}>
          <TabButton
            active={lens === 'filed'}
            label={t('ops.myRequests.filedByMe')}
            count={filedTotal}
            onClick={() => {
              setLens('filed');
            }}
          />
          <TabButton
            active={lens === 'assigned'}
            label={t('ops.myRequests.assignedToMe')}
            count={assignedTotal}
            onClick={() => {
              setLens('assigned');
            }}
          />
        </TabGroup>
        <div className="w-full sm:w-52">
          <Select
            aria-label={t('ops.common.statusFilterLabel')}
            options={statusOptions}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as OpsStatusFilter);
            }}
            fullWidth
            size="sm"
          />
        </div>
      </div>

      <p className="text-sm text-[var(--color-text-muted)]">
        {lens === 'filed' ? (
          <Trans
            i18nKey="ops.myRequests.filedIntro"
            values={{ nick }}
            components={[<span key="nick" className="font-semibold" />]}
          />
        ) : (
          <Trans
            i18nKey="ops.myRequests.assignedIntro"
            values={{ nick }}
            components={[<span key="nick" className="font-semibold" />]}
          />
        )}
      </p>

      {isError && (
        <Card>
          <div className="p-6 text-center">
            <p className="text-sm text-[var(--color-error)]">{t('ops.common.fetchError')}</p>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={() => {
                void refetch();
              }}
            >
              {t('ops.common.retry')}
            </Button>
          </div>
        </Card>
      )}

      {!isError && isLoading && (
        <Card>
          <div className="p-6 text-center text-sm text-[var(--color-text-muted)]">
            {t('ops.myRequests.loading')}
          </div>
        </Card>
      )}

      {!isError && !isLoading && rows.length === 0 && (
        <Card>
          <div className="p-6 text-center text-sm text-[var(--color-text-muted)]">
            {lens === 'filed' ? t('ops.myRequests.emptyFiled') : t('ops.myRequests.emptyAssigned')}
          </div>
        </Card>
      )}

      {!isError &&
        !isLoading &&
        rows.map((r) => (
          <Link key={r.id} to="/ops/requests/$id" params={{ id: r.id }} className="block">
            <Card hoverable padding="sm">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-[var(--color-text-primary)]">
                    #{opsDisplayNo(r.id)} — {r.title}
                  </div>
                  {/* 種別は muted なメタ行に混ぜると見落とされるうえ、生の enum は翻訳されない。 */}
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                    <CategoryBadge category={r.category} />
                    <span className="text-xs text-[var(--color-text-muted)]">
                      {OPS_SERVICE_LABELS[r.service]} · {r.submittedAt.slice(0, 10)} ·{' '}
                      {lens === 'filed'
                        ? t('ops.myRequests.cardMetaFiled', {
                            assignee: r.assigneeName ?? t('ops.common.unassigned'),
                          })
                        : t('ops.myRequests.cardMetaAssigned', { requester: r.requesterName })}
                    </span>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <SlaChip sla={computeOpsSla(r)} />
                  <StateBadge state={r.state} />
                </div>
              </div>
            </Card>
          </Link>
        ))}

      {!isError && !isLoading && hasNextPage && (
        <div className="flex justify-center pt-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            loading={isFetchingNextPage}
            onClick={() => {
              void fetchNextPage();
            }}
          >
            {t('ops.common.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
